import * as Sentry from '@sentry/react';

import {
  buildFunctionInvokeError,
  FunctionInvokeError,
  getFunctionInvokeErrorSentryContext,
  getFunctionInvokeErrorSentryFingerprint,
  getFunctionInvokeSentryTitle,
  isExpectedRecoveryPathDenial,
  isFunctionInvokeAbortError,
  isOfflineFunctionFetchError,
} from '@/lib/functionInvokeErrors';

type BreachNotificationInvocationContext = {
  action: string;
  portal?: string;
};

const EXPECTED_BREACH_NOTIFICATION_CLIENT_REJECTION_STATUSES = new Set([400, 401, 409]);
const EXPECTED_BREACH_NOTIFICATION_NOT_FOUND_CODES = new Set([
  'breach_case_not_found',
  'breach_evidence_not_found',
  'breach_notice_not_found',
  'news_alert_not_found',
  'supplier_alert_not_found',
  'vendor_profile_not_found',
]);

function getSafeInvocationContext(context: BreachNotificationInvocationContext) {
  return {
    action: /^[a-z_]+$/.test(context.action) ? context.action : 'unknown',
    portal: context.portal === 'customer' || context.portal === 'vendor' ? context.portal : 'unknown',
  };
}

function isExpectedBreachNotificationDenial(error: FunctionInvokeError): boolean {
  if (error.status === 403) {
    return isExpectedRecoveryPathDenial(error) || error.errorCode === 'feature_not_enabled';
  }

  if (error.status === 404) {
    return typeof error.errorCode === 'string'
      && EXPECTED_BREACH_NOTIFICATION_NOT_FOUND_CODES.has(error.errorCode);
  }

  return typeof error.status === 'number'
    && EXPECTED_BREACH_NOTIFICATION_CLIENT_REJECTION_STATUSES.has(error.status);
}

export function addBreachNotificationInvocationBreadcrumb(
  context: BreachNotificationInvocationContext,
): void {
  Sentry.addBreadcrumb({
    category: 'breach-notification-cases.invoke',
    data: getSafeInvocationContext(context),
    level: 'info',
    message: 'Invoking Breach Centre operation',
    type: 'default',
  });
}

export async function captureBreachNotificationInvokeFailure(
  error: unknown,
  context: BreachNotificationInvocationContext,
): Promise<void> {
  if (isFunctionInvokeAbortError(error) || isOfflineFunctionFetchError(error)) {
    return;
  }

  const fallback = 'Breach Centre operation failed';
  const normalizedError = error instanceof FunctionInvokeError
    ? error
    : await buildFunctionInvokeError('breach-notification-cases', error, fallback);

  if (isExpectedBreachNotificationDenial(normalizedError)) {
    return;
  }

  const safeContext = getSafeInvocationContext(context);
  const sentryContext = await getFunctionInvokeErrorSentryContext(normalizedError, {
    tags: {
      feature: 'breach-centre',
      'breach.action': safeContext.action,
      'breach.portal': safeContext.portal,
    },
  });
  const sentryFingerprint = await getFunctionInvokeErrorSentryFingerprint(normalizedError);
  const sentryMessage = getFunctionInvokeSentryTitle(normalizedError, fallback);

  Sentry.captureException(new Error(sentryMessage), {
    ...sentryContext,
    fingerprint: sentryFingerprint,
  });
}

// ENT-2733 (Codex P2 round 4): a bulk selection fans out into per-chunk backend
// calls, and setNews/SupplierAlertStatusBulk fold a failed chunk into per-item
// results so partial success is preserved and the batch resolves. That fold makes
// the page's exception path unreachable, so without an explicit capture here an
// unexpected transport / whole-function failure (e.g. a connection reset the
// backend never observes) would vanish from Sentry along with the normalized
// FunctionInvokeError metadata (status, errorCode, errorClass, requestId). Capture
// unexpected chunk failures with the canonical function-invoke context/fingerprint;
// skip user-transient ones (request aborted, offline) that are not actionable.
export async function captureBulkDispositionChunkFailure(
  error: unknown,
  context: { action: string; organizationId: string; batchSize: number },
): Promise<void> {
  if (isFunctionInvokeAbortError(error) || isOfflineFunctionFetchError(error)) {
    return;
  }

  const fallback = 'Bulk alert status chunk failed';
  const normalizedError = error instanceof FunctionInvokeError
    ? error
    : await buildFunctionInvokeError('breach-notification-cases', error, fallback);

  if (isExpectedBreachNotificationDenial(normalizedError)) {
    return;
  }

  const sentryContext = await getFunctionInvokeErrorSentryContext(normalizedError, {
    tags: {
      feature: 'breach-bulk-disposition',
      'bulk.action': context.action,
    },
    extra: {
      organizationId: context.organizationId,
      batchSize: context.batchSize,
    },
  });
  const sentryFingerprint = await getFunctionInvokeErrorSentryFingerprint(normalizedError);
  const sentryMessage = getFunctionInvokeSentryTitle(normalizedError, fallback);

  Sentry.captureException(new Error(sentryMessage), {
    ...sentryContext,
    fingerprint: sentryFingerprint,
  });
}
